import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { applicationsApi } from '../services/api';

const ApplicationView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  useEffect(() => {
    fetchApplication();
  }, [id]);

  const fetchApplication = async () => {
    try {
      setLoading(true);
      const response = await applicationsApi.getById(id);
      const data = response.data?.application || response.data || {};
      setApplication(data);
      setSelectedStatus(data.status || '');
      setError('');
    } catch (err) {
      setError('Failed to load application details');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    try {
      await applicationsApi.updateStatus(id, selectedStatus);
      alert('Application status updated successfully!');
      fetchApplication(); // Refresh data
    } catch (err) {
      alert('Failed to update status');
      console.error('Update error:', err);
    }
  };

  // Safe field extractor helper
  const getField = (app, ...keys) => {
    if (!app) return '';
    for (const key of keys) {
      if (app[key] !== undefined && app[key] !== null && app[key] !== '') {
        return app[key];
      }
    }
    return '';
  };

  // Helper to get all documents with absolute backend/Cloudinary URLs
  const getDocuments = () => {
    if (!application) return [];
    
    const docs = [];
    const API_BASE_URL = 'https://naus-backend.onrender.com';
    
    const formatUrl = (rawPath) => {
      if (!rawPath) return '';
      // If it's already an absolute URL (Cloudinary or external), return as is
      if (rawPath.startsWith('http://') || rawPath.startsWith('https://')) {
        return rawPath;
      }
      // Clean up duplicate leading slashes or upload directories
      let cleaned = rawPath.replace(/^\/+/, '');
      if (cleaned.startsWith('uploads/')) {
        cleaned = cleaned.replace(/^uploads\//, '');
      }
      return `${API_BASE_URL}/uploads/${cleaned}`;
    };

    const mbbsCert = getField(application, 'mbbsCertificate', 'mbbscertificate', 'mbbs_certificate');
    if (mbbsCert) {
      docs.push({
        filename: mbbsCert.split('/').pop(),
        url: formatUrl(mbbsCert),
        type: 'MBBS Certificate'
      });
    }

    const fellowshipCert = getField(application, 'fellowshipCertificate', 'fellowshipcertificate', 'fellowship_certificate');
    if (fellowshipCert) {
      docs.push({
        filename: fellowshipCert.split('/').pop(),
        url: formatUrl(fellowshipCert),
        type: 'Fellowship Certificate'
      });
    }

    if (Array.isArray(application.documents)) {
      application.documents.forEach((doc, index) => {
        const rawUrl = doc.url || doc.filename || doc;
        docs.push({
          filename: typeof rawUrl === 'string' ? rawUrl.split('/').pop() : `Document ${index + 1}`,
          url: formatUrl(rawUrl),
          type: doc.type || `Additional Document ${index + 1}`
        });
      });
    }

    return docs;
  };

  if (loading) return <div style={{ padding: '2rem' }}>Loading application...</div>;
  if (error) return <div style={{ padding: '2rem', color: 'red' }}>{error}</div>;
  if (!application) return <div style={{ padding: '2rem' }}>Application not found</div>;

  const documents = getDocuments();

  // Extract display values with fallback keys
  const firstName = getField(application, 'firstName', 'firstname', 'first_name');
  const middleName = getField(application, 'middleName', 'middlename', 'middle_name');
  const lastName = getField(application, 'lastName', 'lastname', 'last_name');
  const fullName = `${firstName} ${middleName} ${lastName}`.trim() || getField(application, 'name', 'fullname', 'full_name') || 'N/A';

  const email = getField(application, 'email') || 'N/A';
  const phone = getField(application, 'phoneNumber', 'phonenumber', 'phone_number', 'phone') || 'N/A';
  const specialty = getField(application, 'areaOfSpecialty', 'areaofspecialty', 'area_of_specialty', 'specialty') || 'N/A';
  const mdcnReg = getField(application, 'mdcnRegistrationNumber', 'mdcnregistrationnumber', 'mdcn_registration_number', 'mdnRegistration') || 'N/A';
  const mbbsYear = getField(application, 'yearQualifiedMBBS', 'yearqualifiedmbbs', 'year_qualified_mbbs', 'mbbsYear') || 'N/A';

  const streetAddress = getField(application, 'streetAddress', 'streetaddress', 'street_address') || 'N/A';
  const permanentAddress = getField(application, 'permanentAddress', 'permanentaddress', 'permanent_address') || 'N/A';

  const fellowshipCollege = getField(application, 'fellowshipCollege', 'fellowshipcollege', 'fellowship_college') || 'N/A';
  const fwacs = getField(application, 'fwacs');
  const fmcs = getField(application, 'fmcs');
  const facs = getField(application, 'facs');
  const frcs = getField(application, 'frcs');
  const others = getField(application, 'others');
  const qualificationYear = getField(application, 'qualificationYear', 'qualificationyear', 'qualification_year') || 'N/A';
  const additionalQualification = getField(application, 'additionalQualification', 'additionalqualification', 'additional_qualification') || 'N/A';
  const residencyTraining = getField(application, 'residencyTraining', 'residencytraining', 'residency_training') || 'N/A';
  const foreignInstitution = getField(application, 'foreignInstitution', 'foreigninstitution', 'foreign_institution') || 'N/A';

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h1>Application Details</h1>
        <button
          onClick={() => navigate('/dashboard')}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Back to Dashboard
        </button>
      </div>

      {/* Personal Information */}
      <div style={{ marginBottom: '2rem', padding: '1.5rem', border: '1px solid #dee2e6', borderRadius: '8px' }}>
        <h2 style={{ color: '#006400', marginBottom: '1rem' }}>Personal Information</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div><strong>Full Name:</strong> {fullName}</div>
          <div><strong>Email:</strong> {email}</div>
          <div><strong>Phone:</strong> {phone}</div>
          <div><strong>Specialty:</strong> {specialty}</div>
          <div><strong>MDN Registration:</strong> {mdcnReg}</div>
          <div><strong>MBBS Year:</strong> {mbbsYear}</div>
        </div>
      </div>

      {/* Address Information */}
      <div style={{ marginBottom: '2rem', padding: '1.5rem', border: '1px solid #dee2e6', borderRadius: '8px' }}>
        <h2 style={{ color: '#006400', marginBottom: '1rem' }}>Address</h2>
        <div><strong>Street Address:</strong> {streetAddress}</div>
        <div><strong>Permanent Address:</strong> {permanentAddress}</div>
      </div>

      {/* Qualifications */}
      <div style={{ marginBottom: '2rem', padding: '1.5rem', border: '1px solid #dee2e6', borderRadius: '8px' }}>
        <h2 style={{ color: '#006400', marginBottom: '1rem' }}>Qualifications</h2>
        <div><strong>Fellowship College:</strong> {fellowshipCollege}</div>
        <div><strong>FWACS:</strong> {fwacs ? 'Yes' : 'No'}</div>
        <div><strong>FMCS:</strong> {fmcs ? 'Yes' : 'No'}</div>
        <div><strong>FACS:</strong> {facs ? 'Yes' : 'No'}</div>
        <div><strong>FRCS:</strong> {frcs ? 'Yes' : 'No'}</div>
        <div><strong>Others:</strong> {others ? 'Yes' : 'No'}</div>
        <div><strong>Qualification Year:</strong> {qualificationYear}</div>
        <div><strong>Additional Qualification:</strong> {additionalQualification}</div>
        <div><strong>Residency Training:</strong> {residencyTraining}</div>
        <div><strong>Foreign Institution:</strong> {foreignInstitution}</div>
      </div>

      {/* Documents Section */}
      <div style={{ marginBottom: '2rem', padding: '1.5rem', border: '1px solid #dee2e6', borderRadius: '8px' }}>
        <h2 style={{ color: '#006400', marginBottom: '1rem' }}>Submitted Documents</h2>
        {documents.length > 0 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
            {documents.map((doc, index) => (
              <div key={index} style={{ 
                border: '1px solid #ddd', 
                borderRadius: '4px', 
                padding: '1rem',
                minWidth: '200px',
                backgroundColor: '#ffffff'
              }}>
                <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                  <strong>{doc.type}</strong>
                </div>
                <div style={{ fontSize: '0.875rem', marginBottom: '0.5rem', color: '#666', wordBreak: 'break-all' }}>
                  {doc.filename}
                </div>
                <a
                  href={doc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  style={{
                    color: '#006400',
                    textDecoration: 'underline',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'inline-block',
                    padding: '0.25rem 0.5rem',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px',
                    border: '1px solid #006400'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  📄 View / Download Document
                </a>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '1rem', color: '#666' }}>
            No documents submitted
          </div>
        )}
      </div>

      {/* Status Management */}
      <div style={{ marginBottom: '2rem', padding: '1.5rem', border: '1px solid #dee2e6', borderRadius: '8px' }}>
        <h2 style={{ color: '#006400', marginBottom: '1rem' }}>Application Status</h2>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            style={{
              padding: '0.5rem',
              borderRadius: '4px',
              border: '1px solid #ced4da',
              minWidth: '150px',
              fontSize: '0.9rem'
            }}
          >
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          <button
            onClick={handleStatusUpdate}
            disabled={selectedStatus === application.status}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: selectedStatus === application.status ? '#6c757d' : '#006400',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: selectedStatus === application.status ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Update Status
          </button>
        </div>
        <div style={{ marginTop: '1rem', fontSize: '0.9rem' }}>
          <strong>Current Status:</strong> {application.status}
        </div>
      </div>
    </div>
  );
};

export default ApplicationView;